/**
 * @file DraftView.tsx
 * @description
 * Renders the UI for a submission in DRAFT status.
 *
 * Key features:
 * - "Continue Editing" button that navigates to SubmissionForm to edit the draft.
 * - "Delete Draft" button that calls `deleteDraftSubmission` and returns the user to the dashboard.
 * - "Download PDF" button that generates a PDF Blob and downloads it to the user's machine
 *   (avoiding session problems caused by opening new tabs).
 *
 * @dependencies
 * - React Router: for navigation
 * - docGenerationService: to generate PDF
 * - submissionService: to delete the draft submission
 *
 * @notes
 * - We replaced any new tab opening with a direct download approach,
 *   so there's no possible cross-tab session interference.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteDraftSubmission } from '../../services/submissionService';
import { generatePdfFromFormData } from '../../services/docGenerationService';
//import { useSupabase } from '../../contexts/SupabaseContext';
import { SubmissionRow } from '../../types/submission';
import { useFormProgress } from '../../hooks/useFormProgress';
import { formSections } from '../../components/FormSubmissionFields';

interface DraftViewProps {
  submission: SubmissionRow;
}

/**
 * DraftView
 * Displays actions for a DRAFT submission:
 * 1) Continue editing
 * 2) Delete the draft
 * 3) Download PDF
 */
const DraftView: React.FC<DraftViewProps> = ({ submission }) => {
  const navigate = useNavigate();
  const [error, setError] = React.useState<string | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = React.useState(false);
  
  // Create a memoized supabase client
  //const supabase = useSupabase();

  // Calculate form completion percentage using the same hook as in SubmissionFormDraft
  const { overallCompletion } = useFormProgress(formSections, submission.data || {});

  /**
   * Handle "Continue Editing" - user is taken to SubmissionForm in "edit mode"
   * We pass the submission ID as a query param "edit".
   */
  const handleContinueEditing = () => {
    navigate(`/new-submission?edit=${submission.id}`);
  };

  /**
   * Handle "Delete Draft" - shows confirmation dialog, then calls deleteDraftSubmission,
   * and navigates to dashboard on success.
   */
  const handleDeleteDraft = async () => {
    // Show confirmation dialog first
    const confirmDelete = window.confirm("Are you sure you want to delete this submission? This action cannot be undone!");
    
    // Only proceed if user confirmed
    if (!confirmDelete) return;

    setIsDeleting(true);
    setError(null);
    try {
      await deleteDraftSubmission(submission.id, submission.user_id);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Failed to delete draft.');
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Handle "Download PDF" - calls docGenerationService, then triggers a direct download
   * using a hidden anchor element with `download` attribute.
   */
  const handleDownloadPdf = async () => {
    setIsGeneratingPdf(true);
    setError(null);
    try {
      // Pass formSections as the second argument
      const pdfBlob = await generatePdfFromFormData(submission.data || {}, formSections);
      
      // Create an object URL for the blob
      const blobUrl = URL.createObjectURL(pdfBlob);

      // Create a temporary <a> to force download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'draft_submission.pdf';

      // Programmatically click the link to trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Cleanup the URL object
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 30000);
    } catch (err: any) {
      setError(err.message || 'Failed to generate PDF for download.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    <div>
      <div className="text-gray-500 mt-2">
        <p>
          This submission is in <strong>DRAFT</strong> status. Use "Continue Editing" to fill out the
          submission form, or "Download PDF" if you want to save a partial snapshot locally.
        </p>
        <p className="mt-2">
          If you no longer need this IRB application, you may permanently discard it with "Delete Draft."
        </p>
      </div>
      {error && (
        <div className="mb-4 px-4 py-2 border-l-4 border-red-500 bg-red-50 text-red-800">
          {error}
        </div>
      )}

      <div className="space-y-3 mt-6">
        {/* Continue Editing with completion indicator */}
        <div className="flex items-center">
          <button
            onClick={handleContinueEditing}
            className="inline-flex items-center justify-center bg-[#F55D3E] text-white rounded-md px-4 py-2 font-bold hover:bg-[#d94a30] focus:outline-none focus:ring-2 focus:ring-[#F55D3E]"
            disabled={isDeleting || isGeneratingPdf}
          >
            Continue Editing
          </button>
          
          {/* Form completion indicator */}
          <div className="ml-4">
            <div className="flex items-center">
              <span className="text-sm font-medium mr-2">
                {overallCompletion}% complete
              </span>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                overallCompletion === 100 
                  ? 'bg-green-100 text-green-800' 
                  : overallCompletion >= 50 
                    ? 'bg-yellow-100 text-yellow-800' 
                    : 'bg-red-100 text-red-800'
              }`}>
                {overallCompletion === 100 ? 'Ready' : 'In Progress'}
              </span>
            </div>
            <div className="w-36 h-2 bg-gray-200 rounded-full mt-1">
              <div
                className={`h-2 rounded-full transition-all duration-300 ease-in-out ${
                  overallCompletion === 100 
                    ? 'bg-green-500' 
                    : overallCompletion >= 50 
                      ? 'bg-yellow-500' 
                      : 'bg-red-500'
                }`}
                style={{ width: `${overallCompletion}%` }}
              />
            </div>
          </div>
        </div>
        
        <br />
        {/* Download PDF */}
        <button
          onClick={handleDownloadPdf}
          className="inline-flex items-center justify-center bg-gray-700 text-white rounded-md px-4 py-2 font-bold hover:bg-gray-350 focus:outline-none focus:ring-2 focus:ring-gray-700"
          disabled={isDeleting || isGeneratingPdf}
        >
          {isGeneratingPdf ? 'Generating PDF...' : 'Download PDF'}
        </button>
        <br />
        {/* Delete Draft */}
        <button
          onClick={handleDeleteDraft}
          className="inline-flex items-center justify-center bg-red-500 text-white rounded-md px-4 py-2 font-semibold hover:bg-red-250 focus:outline-none focus:ring-2 focus:ring-red-500"
          disabled={isDeleting || isGeneratingPdf}
        >
          {isDeleting ? 'Deleting...' : 'Delete Draft'}
        </button>
      </div>
    </div>
  );
};

export default DraftView;
